import { scrollInstantly } from "../scrollInstantly"

/* EGDE-719 (r3): one continuous trip to the anchor, instead of a travel, a
   pause, and a second travel.

   What QA filmed twice — "there is animation but it's lagging", then "still
   lags" — is not a slow animation. It is two of them with a dead screen in
   between. Traced on the homepage at 1512x860, /#contact-us:

     154ms   the browser's fragment scroll sets off, aimed at y=3555 — where
             the anchor sits at that instant
     820ms   it arrives at 3555 and stops. The anchor is at 4413 by now: the
             blocks above it hydrated and their images took their real height
             while the scroll was in the air
     1003ms  183ms of nothing, which is how long the old "wait for the scroll
             to go quiet" watcher needs to be sure it has stopped
     1470ms  a second eased scroll covers the missing 858px

   Chasing a landing after the fact can only ever look like that, because the
   browser commits to a destination it measured before the page had finished
   growing. So this stops treating the drift as a correction and folds it into
   the journey: the trip is aimed at the anchor's position AS OF THIS FRAME. The
   page growing underfoot just moves where it is heading — the motion never
   stops to notice.

   Two properties are doing the work, and both are load-bearing:

   - It sets off at the speed it finds. The browser's scroll starts long before
     React hydrates (154ms against a mount that lands wherever the connection
     puts it), so this always takes over something already moving. A tween that
     started from rest would brake first, which is the stumble all over again.
   - It arrives on the frame it promised. A spring was the obvious fit for a
     destination that moves, but its exponential tail crawls the last few
     hundred pixels a pixel at a time — measured, 900ms of motion followed by
     two more seconds of creep. A fixed duration ends dead on the anchor. */

/** Seconds the remaining distance should take. Short hops must not feel
 *  sluggish and long ones must not feel like a fall, so it grows with the
 *  distance and then stops. */
function durationFor(distance: number) {
  return Math.min(0.8, Math.max(0.3, 0.2 + distance / 7000))
}

/** Frames are capped at this so a backgrounded tab resuming after seconds
 *  cannot seed the trip with a nonsense speed. */
const MAX_SAMPLE_MS = 50

/** Nothing may glide forever, however hard the layout fights. */
const HARD_CAP_MS = 2500

/** Where the target wants to sit: its own `scroll-margin-top`. */
function offsetOf(target: Element) {
  return parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0
}

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

/**
 * Glides the window to `target`, re-reading where the target is on every frame,
 * and calls `onArrive` once it has.
 *
 * Returns a canceller. A visitor's own scroll input is NOT handled here — the
 * caller owns that decision, because the same gesture means different things
 * mid-load and mid-click.
 */
export function glideToAnchor(target: Element, onArrive: () => void) {
  const destination = () =>
    target.getBoundingClientRect().top + window.scrollY - offsetOf(target)

  const arrive = () => {
    scrollInstantly(() => window.scrollTo({ top: destination() }))
    onArrive()
  }

  // The opt-out lives here rather than in `scroll-behavior`, because every
  // scroll this makes is issued instantly — the CSS never sees them.
  if (prefersReducedMotion()) {
    arrive()
    return () => {}
  }

  let raf = 0
  let cancelled = false

  // Two samples before the first step: the second is where the trip starts
  // from, and the pair gives the speed it starts at. Nothing is scrolled while
  // sampling, so whatever is already running keeps running and the hand-over
  // happens at its own pace.
  let sampleAt = 0
  let sampleY = 0
  let startY = 0
  let startV = 0
  let startAt = 0
  let duration = 0

  const stop = () => {
    cancelled = true
    if (raf) cancelAnimationFrame(raf)
    raf = 0
  }

  const step = (now: number) => {
    raf = 0
    if (cancelled) return

    if (!startAt) {
      if (!sampleAt) {
        sampleAt = now
        sampleY = window.scrollY
        raf = requestAnimationFrame(step)
        return
      }
      const dt = (now - sampleAt) / 1000
      startY = window.scrollY
      startV =
        dt > 0 && now - sampleAt <= MAX_SAMPLE_MS ? (startY - sampleY) / dt : 0
      startAt = now
      duration = durationFor(Math.abs(destination() - startY))
      raf = requestAnimationFrame(step)
      return
    }

    const s = Math.min(1, (now - startAt) / 1000 / duration)
    const dest = destination()

    if (s >= 1 || now - startAt >= HARD_CAP_MS) {
      stop()
      arrive()
      return
    }

    // Cubic Hermite from (startY, startV) to (dest, 0). Reading `dest` fresh
    // each frame is what absorbs the document growing: the basis weight on it
    // rises from nothing to everything, so growth early barely registers and
    // growth late is followed exactly — which is the only way to land on an
    // anchor that is still moving when the trip begins.
    const s2 = s * s
    const s3 = s2 * s
    const fromPosition = 2 * s3 - 3 * s2 + 1
    const fromVelocity = s3 - 2 * s2 + s
    const toPosition = -2 * s3 + 3 * s2
    const y =
      fromPosition * startY +
      fromVelocity * duration * startV +
      toPosition * dest

    // Instant, always: `scroll-behavior: smooth` still applies to the plain
    // fragment scrolls this takes over from, and sixty animated steps chasing
    // each other is the very stutter this exists to remove.
    scrollInstantly(() => window.scrollTo({ top: y }))
    raf = requestAnimationFrame(step)
  }

  raf = requestAnimationFrame(step)
  return stop
}
